import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Grid3x3, Trash2, Eraser, Printer, LayoutGrid, Loader2, Share2, Wand2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { autoArrange, stats as arrangeStats } from "@/lib/autoArrange";
import { CompanionHint } from "@/components/CompanionHint";
import { ShareCard } from "@/components/ShareCard";
import { neighbourTint } from "@/lib/gardenChecks";
import { shareNodeAsPng } from "@/lib/shareImage";
import { Button } from "@/components/ui/button";

export function MiniGarden() {
  const [state, setState] = useState(null);
  const [selected, setSelected] = useState(null);
  const [over, setOver] = useState(null);
  const drag = useRef(null);
  const navigate = useNavigate();
  const [converting, setConverting] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [preview, setPreview] = useState(null);
  const { user } = useAuth();
  const shareRef = useRef(null);

  const share = async () => {
    setSharing(true);
    try {
      await shareNodeAsPng(shareRef.current, "My Garden Grid");
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Could not create the image. Please try again.");
    } finally {
      setSharing(false);
    }
  };

  const toPlan = async () => {
    setConverting(true);
    try {
      const { data } = await api.post("/dashboard/mini-garden/to-plan");
      toast.success("Layout plan created");
      navigate(`/planner?plan=${data.id}`);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d?.plan_id) {
        toast(d.message, { action: { label: "Open plan", onClick: () => navigate(`/planner?plan=${d.plan_id}`) } });
      } else toast.error("Could not create a Layout plan");
    } finally {
      setConverting(false);
    }
  };

  useEffect(() => {
    api.get("/dashboard/mini-garden").then(({ data }) => setState(data)).catch(() => toast.error("Could not load your garden grid"));
  }, []);

  const save = (cells) => {
    setState((s) => ({ ...s, cells }));
    api.put("/dashboard/mini-garden", { cells }).catch(() => toast.error("Could not save grid"));
  };

  const iconOf = (slug) => state.palette.find((p) => p.slug === slug);

  const dropOnCell = (key) => {
    const d = drag.current;
    drag.current = null;
    setOver(null);
    if (!d) return;
    const cells = { ...state.cells };
    if (d.from) delete cells[d.from];
    cells[key] = d.slug;
    save(cells);
  };

  const dropOnPalette = () => {
    const d = drag.current;
    drag.current = null;
    if (!d?.from) return;
    const cells = { ...state.cells };
    delete cells[d.from];
    save(cells);
  };

  const tapCell = (key) => {
    const cells = { ...state.cells };
    if (selected === "erase" || (!selected && cells[key])) delete cells[key];
    else if (selected) cells[key] = selected;
    else return;
    save(cells);
  };

  if (!state) return <div className="lg:col-span-2 h-72 rounded-2xl bg-card border border-border animate-pulse" />;

  const keys = [];
  for (let r = 0; r < state.rows; r++) for (let c = 0; c < state.cols; c++) keys.push(`${c}-${r}`);
  const count = Object.keys(state.cells).length;
  const bySlug = Object.fromEntries(state.palette.map((p) => [p.slug, p]));
  const sel = selected && selected !== "erase" ? bySlug[selected] : null;
  const vegAt = (c, r) => bySlug[state.cells[`${c}-${r}`]];
  const toItems = (cells) => Object.entries(cells).map(([k, slug]) => { const [c, r] = k.split("-").map(Number); return { slug, col: c, row: r }; });

  const startArrange = () => {
    if (!user?.is_premium) {
      toast("Auto Arrange is a Pro feature", { action: { label: "Upgrade", onClick: () => navigate("/pricing") } });
      return;
    }
    const items = toItems(state.cells);
    if (items.length < 2) { toast.info("Place at least two crops first."); return; }
    const next = autoArrange(items, bySlug);
    setPreview({ cells: Object.fromEntries(next.map((p) => [`${p.col}-${p.row}`, p.slug])), before: arrangeStats(items, bySlug), after: arrangeStats(next, bySlug) });
  };

  const applyArrange = () => {
    const prev = state.cells;
    save(preview.cells);
    setPreview(null);
    toast.success("Grid rearranged", { action: { label: "Undo", onClick: () => save(prev) } });
  };

  const shown = preview ? preview.cells : state.cells;

  const sharePlan = {
    name: "My Garden Grid",
    beds: [{ id: "grid", name: "Garden Grid", type: "raised", cols: state.cols, rows: state.rows,
      placements: Object.entries(state.cells).map(([k, slug]) => { const [c, r] = k.split("-").map(Number); return { slug, col: c, row: r }; }) }],
  };

  return (
    <div className="lg:col-span-2 bg-card rounded-2xl border border-border p-5 sm:p-6 shadow-sm" data-testid="mini-garden">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <h2 className="font-display text-lg font-semibold flex items-center gap-2 whitespace-nowrap">
          <Grid3x3 className="h-5 w-5 text-emerald-600" /> My Garden Grid
        </h2>
        <div className="flex flex-wrap items-center justify-end gap-1.5">
          <span className="text-xs text-muted-foreground whitespace-nowrap" data-testid="mini-garden-count">{count} planted</span>
          <Button size="sm" variant="outline" onClick={toPlan} disabled={converting || count === 0} className="h-8 rounded-xl" data-testid="mini-garden-to-plan" title="Turn into a Layout plan">
            {converting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LayoutGrid className="h-3.5 w-3.5" />}<span className="hidden sm:inline ml-1">Make Layout</span>
          </Button>
          <Button size="sm" variant="outline" onClick={startArrange} disabled={count < 2 || !!preview} className="h-8 rounded-xl" data-testid="mini-garden-arrange" title="Auto arrange for companions">
            <Wand2 className="h-3.5 w-3.5" /><span className="hidden sm:inline ml-1">Arrange</span>
          </Button>
          <Button size="sm" variant="outline" onClick={share} disabled={sharing || count === 0} className="h-8 rounded-xl" data-testid="mini-garden-share" title="Share as image">
            {sharing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Share2 className="h-3.5 w-3.5" />}<span className="hidden sm:inline ml-1">Share</span>
          </Button>
          <Button size="sm" variant="outline" onClick={() => window.print()} disabled={count === 0} className="h-8 rounded-xl" data-testid="mini-garden-print" title="Print grid">
            <Printer className="h-3.5 w-3.5" /><span className="hidden sm:inline ml-1">Print</span>
          </Button>
          <Button size="sm" variant={selected === "erase" ? "default" : "outline"} onClick={() => setSelected((s) => (s === "erase" ? null : "erase"))}
            className={`h-8 rounded-xl ${selected === "erase" ? "bg-rose-600 hover:bg-rose-700" : ""}`} data-testid="mini-garden-erase">
            <Eraser className="h-3.5 w-3.5" />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => count && window.confirm("Clear the whole grid?") && save({})} className="h-8 rounded-xl text-muted-foreground" data-testid="mini-garden-clear">
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      {preview && (
        <div className="mb-2 flex flex-wrap items-center gap-2 rounded-xl border border-violet-300 bg-violet-50 dark:bg-violet-950/30 dark:border-violet-800 p-2.5 text-sm" data-testid="mini-arrange-preview">
          <Wand2 className="h-4 w-4 text-violet-600" />
          <span className="flex-1 min-w-[180px]" data-testid="mini-arrange-stats">Preview: good pairs {preview.before.good} → <strong>{preview.after.good}</strong>, bad pairs {preview.before.bad} → <strong>{preview.after.bad}</strong></span>
          <Button size="sm" className="h-8 rounded-xl bg-emerald-700 hover:bg-emerald-800" onClick={applyArrange} data-testid="mini-arrange-apply">Apply</Button>
          <Button size="sm" variant="outline" className="h-8 rounded-xl" onClick={() => setPreview(null)} data-testid="mini-arrange-cancel">Cancel</Button>
        </div>
      )}
      <div
        className={`grid gap-1 rounded-xl p-1.5 ${preview ? "pointer-events-none ring-2 ring-violet-400" : ""} bg-[repeating-linear-gradient(45deg,#f5f0e6_0_6px,#efe8d8_6px_12px)] dark:bg-[repeating-linear-gradient(45deg,#1f2a22_0_6px,#243029_6px_12px)] border-2 border-emerald-900/15 dark:border-white/10`}
        style={{ gridTemplateColumns: `repeat(${state.cols}, minmax(0, 1fr))` }}
        data-testid="mini-garden-grid"
      >
        {keys.map((k) => {
          const v = shown[k] && iconOf(shown[k]);
          const [c, r] = k.split("-").map(Number);
          const tint = sel && !v ? neighbourTint(sel, c, r, vegAt) : null;
          return (
            <button
              data-tint={tint || undefined}
              key={k}
              data-testid={`mini-cell-${k}`}
              onClick={() => tapCell(k)}
              onDragOver={(e) => { e.preventDefault(); setOver(k); }}
              onDragLeave={() => setOver((o) => (o === k ? null : o))}
              onDrop={(e) => { e.preventDefault(); dropOnCell(k); }}
              title={v ? v.name : "Empty"}
              className={`aspect-square rounded-lg border flex items-center justify-center transition-colors ${over === k ? "bg-emerald-200/70 border-emerald-500" : tint === "bad" ? "bg-rose-300/50 border-rose-400" : tint === "good" ? "bg-emerald-300/50 border-emerald-400" : v ? "bg-white/70 dark:bg-white/5 border-emerald-900/10" : "bg-white/30 dark:bg-black/10 border-dashed border-emerald-900/15 hover:bg-emerald-50 dark:hover:bg-emerald-900/20"}`}
            >
              {v && (
                <img src={v.icon} alt={v.name} draggable onDragStart={() => { drag.current = { slug: v.slug, from: k }; }}
                  className="w-[80%] h-[80%] object-contain drop-shadow-sm cursor-grab active:cursor-grabbing" />
              )}
            </button>
          );
        })}
      </div>

      <p className="text-xs text-muted-foreground mt-3 mb-2">Drag veg onto the grid (or tap a veg, then tap squares). Drag a plant back here to remove it.</p>
      <div className="flex gap-2 overflow-x-auto pb-1" data-testid="mini-garden-palette"
        onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); dropOnPalette(); }}>
        {state.palette.map((p) => (
          <button key={p.slug} draggable onDragStart={() => { drag.current = { slug: p.slug, from: null }; }}
            onClick={() => setSelected((s) => (s === p.slug ? null : p.slug))}
            data-testid={`mini-palette-${p.slug}`} title={p.name}
            className={`shrink-0 w-14 rounded-xl border p-1 flex flex-col items-center cursor-grab transition-colors ${selected === p.slug ? "border-emerald-500 ring-2 ring-emerald-500 bg-emerald-50 dark:bg-emerald-900/30" : "border-border hover:border-emerald-300"}`}>
            <img src={p.icon} alt={p.name} className="w-9 h-9 object-contain pointer-events-none" />
            <span className="text-[10px] font-medium truncate w-full text-center">{p.name}</span>
          </button>
        ))}
      </div>
      {sel && <CompanionHint veg={sel} catalog={state.palette} />}
      <GridPrint state={state} keys={keys} iconOf={iconOf} />
      <ShareCard ref={shareRef} plan={sharePlan} vegBySlug={bySlug} families={state.families} subtitle="Garden Grid"
        iconUrl={(slug) => `${process.env.REACT_APP_BACKEND_URL}/api/garden/icon/${slug}`} />
    </div>
  );
}

function GridPrint({ state, keys, iconOf }) {
  const counts = {};
  Object.values(state.cells).forEach((s) => { counts[s] = (counts[s] || 0) + 1; });
  const td = { padding: "6px 8px", borderBottom: "1px solid #ddd", fontSize: 12, textAlign: "left" };
  return createPortal(
    <div id="print-root" data-testid="grid-print" style={{ fontFamily: "Georgia, serif", color: "#1a2e1a", padding: 12 }}>
      <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>My Garden Grid</h1>
      <p style={{ fontSize: 12, color: "#555", margin: "4px 0 16px" }}>Sprout Log · printed {new Date().toLocaleDateString()}</p>
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${state.cols}, 76px)`, border: "2px solid #6b7f5e", borderRadius: 6, width: "max-content" }}>
        {keys.map((k) => {
          const v = state.cells[k] && iconOf(state.cells[k]);
          return (
            <div key={k} style={{ width: 76, height: 76, border: "1px solid #cbd5c0", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
              {v && <><img src={v.icon} alt={v.name} style={{ width: 50, height: 50, objectFit: "contain" }} /><span style={{ fontSize: 9 }}>{v.name}</span></>}
            </div>
          );
        })}
      </div>
      <h2 style={{ fontSize: 16, fontWeight: 700, margin: "18px 0 6px" }}>Plant list</h2>
      <table style={{ borderCollapse: "collapse", minWidth: 280 }}>
        <thead><tr><th style={{ ...td, borderBottom: "2px solid #6b7f5e" }}>Crop</th><th style={{ ...td, borderBottom: "2px solid #6b7f5e" }}>Count</th></tr></thead>
        <tbody>
          {Object.entries(counts).sort((a, b) => b[1] - a[1]).map(([slug, n]) => (
            <tr key={slug}><td style={td}>{iconOf(slug)?.name || slug}</td><td style={td}>{n}</td></tr>
          ))}
        </tbody>
      </table>
    </div>,
    document.body
  );
}
